// Lightweight from-scratch spotlight tour (no library). Highlights real
// elements tagged with [data-tour="…"], dimming the rest via a big box-shadow
// cutout, and walks across pages (it history.push-es to each step's route and
// re-anchors once the target mounts). Step state lives in OnboardingProvider so
// it survives the route change.
import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { C, SANS, MONO } from '../../theme/matteTokens';
import { useOnboarding } from './OnboardingProvider';

const STEPS = [
  { sel: '[data-tour="nav-portfolio"]', path: '/profile/portfolio', title: 'Your Portfolio', body: 'Track every exchange and holding in one place. New accounts start here.' },
  { sel: '[data-tour="connect-exchange"]', path: '/profile/portfolio', title: 'Add an exchange', body: 'Connect Binance or Alpaca with API keys — or create a risk-free demo account in one click.' },
  { sel: '[data-tour="nav-autotrader"]', path: '/profile/traderform', title: 'AutoTrader', body: 'Build and run automated trading bots from your connected exchanges.' },
  { sel: '[data-tour="create-bot"]', path: '/profile/traderform', title: 'Create a bot', body: 'Use Quick Bot to launch a ready-made strategy in seconds, or open the full builder.' },
];

export default function Tour() {
  const { tourRunning, tourStep, nextStep, prevStep, endTour } = useOnboarding();
  const history = useHistory();
  const location = useLocation();
  const [rect, setRect] = useState(null);
  const step = STEPS[tourStep];

  // Navigate to the step's page if we're not already there.
  useEffect(() => {
    if (!tourRunning || !step) return;
    if (location.pathname !== step.path) history.push(step.path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRunning, tourStep]);

  // Locate the target element (poll briefly — it may mount after navigation).
  useEffect(() => {
    if (!tourRunning || !step) { setRect(null); return; }
    let tries = 0;
    const measure = () => {
      const el = document.querySelector(step.sel);
      if (!el) return false;
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) {}
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      return true;
    };
    let intervalId = null;
    if (!measure()) {
      intervalId = setInterval(() => { tries += 1; if (measure() || tries >= 40) { clearInterval(intervalId); } }, 100);
    }
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourRunning, tourStep, location.pathname]);

  if (!tourRunning || !step) return null;

  const pad = 6;
  const last = tourStep === STEPS.length - 1;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const tipW = 340;
  // Place the tooltip below the target if there's room, else above.
  const below = !rect || (rect.top + rect.height + 180 < vh);
  const tipTop = rect ? (below ? rect.top + rect.height + 14 : Math.max(16, rect.top - 170)) : vh / 2 - 90;
  const tipLeft = rect ? Math.max(16, Math.min(rect.left, vw - tipW - 16)) : vw / 2 - tipW / 2;

  return (
    // Outer layer blocks page interaction during the tour; tooltip sits above it.
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      {rect ? (
        <Box sx={{ position: 'fixed', top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, borderRadius: '10px', boxShadow: '0 0 0 9999px rgba(5,8,16,.74)', border: `2px solid ${C.accent}`, pointerEvents: 'none', transition: 'top .2s ease, left .2s ease, width .2s ease, height .2s ease' }} />
      ) : (
        <Box sx={{ position: 'fixed', inset: 0, background: 'rgba(5,8,16,.74)' }} />
      )}

      <Box sx={{ position: 'fixed', top: tipTop, left: tipLeft, width: tipW, maxWidth: 'calc(100vw - 32px)', bgcolor: C.card, border: `1px solid ${C.hairline}`, borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,.5)', p: '16px 18px', fontFamily: SANS, zIndex: 2001 }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.12em', color: C.muted, mb: 0.5 }}>STEP {tourStep + 1} / {STEPS.length}</Typography>
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: C.bright, mb: 0.5 }}>{step.title}</Typography>
        <Typography sx={{ fontSize: '13px', color: C.body, mb: 1.5 }}>{step.body}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: '5px', mr: 'auto' }}>
            {STEPS.map((_, i) => <Box key={i} sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: i === tourStep ? C.accent : 'rgba(255,255,255,.18)' }} />)}
          </Box>
          <Box component="button" onClick={endTour} sx={{ background: 'none', border: 'none', color: C.muted, fontFamily: SANS, fontSize: '12.5px', cursor: 'pointer' }}>Skip</Box>
          {tourStep > 0 && <Box component="button" onClick={prevStep} sx={{ background: 'none', border: `1px solid ${C.hairline}`, color: C.body, fontFamily: SANS, fontSize: '12.5px', borderRadius: '8px', px: '12px', py: '6px', cursor: 'pointer' }}>Back</Box>}
          <Box component="button" onClick={last ? endTour : nextStep} sx={{ background: C.accent, border: 'none', color: '#06121f', fontFamily: SANS, fontWeight: 700, fontSize: '12.5px', borderRadius: '8px', px: '14px', py: '6px', cursor: 'pointer' }}>{last ? 'Done' : 'Next'}</Box>
        </Box>
      </Box>
    </Box>
  );
}
